import { useEffect, useState } from "react";
import {
  BOTTOM_RESERVE,
  BOTTOM_RESERVE_TYPING,
  CHROME_BOTTOM,
  KEYBOARD_INSET,
  VIEWPORT_HEIGHT,
} from "./chrome.js";

/**
 * Is a keyboard covering the bottom of the screen, and by how much.
 *
 * Asked because the tab bar and the keyboard want the same 84 pixels, and on a
 * phone the conversation is what loses them. While somebody is typing, the bar
 * is the least useful thing on screen: they are not going anywhere, they are
 * writing.
 *
 * **Measured, not inferred from focus.** A focused field is not a keyboard —
 * on a laptop, or a phone with a hardware keyboard, nothing covers anything and
 * hiding the navigation would be a bug rather than a courtesy. The visual
 * viewport is the only thing that actually knows.
 *
 * The threshold is there because the visual viewport also moves by a few dozen
 * pixels for an address bar sliding in and out, and a tab bar that blinked at
 * every scroll would be worse than one that never moved.
 */
const KEYBOARD_MIN = 120;

/**
 * Is somebody typing on something with no keyboard of its own?
 *
 * The measurement below is the honest answer to «сколько закрыто», and it is
 * the only thing that can size a pane. It is *not* a reliable answer to «есть
 * ли клавиатура»: on an iPhone in standalone mode the window resizes along
 * with the visual viewport, so the difference the measurement looks for can
 * come out at nothing while a keyboard fills half the screen — which left the
 * tab bar sitting between the composer and the keys.
 *
 * A focused text field is the signal that does not depend on measuring
 * anything. It is wrong on a laptop, where a focused field covers nothing and
 * hiding the navigation would be a bug rather than a courtesy — so it is asked
 * only where there is no keyboard to focus *with*: `pointer: coarse` is a
 * touch screen, and a touch screen that has a text field focused has a
 * keyboard over it.
 */
export function useTypingOnGlass(): boolean {
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    if (!window.matchMedia("(pointer: coarse)").matches) return;

    const look = () => {
      const active = document.activeElement;
      const tag = active?.tagName;
      setTyping(
        tag === "INPUT" ||
          tag === "TEXTAREA" ||
          (active instanceof HTMLElement && active.isContentEditable),
      );
    };

    /*
     * Captured, and a tick late.
     *
     * `focus` and `blur` do not bubble, so they are caught on the way down
     * instead — the field being watched is four screens below this. And a tick
     * late because on the way out of one field into another, `document.
     * activeElement` is briefly the body: reading it in the handler would put
     * the bar back for one frame between two taps.
     */
    let soon: ReturnType<typeof setTimeout> | null = null;
    const later = () => {
      if (soon) clearTimeout(soon);
      soon = setTimeout(look, 0);
    };

    look();
    document.addEventListener("focus", later, true);
    document.addEventListener("blur", later, true);

    return () => {
      if (soon) clearTimeout(soon);
      document.removeEventListener("focus", later, true);
      document.removeEventListener("blur", later, true);
    };
  }, []);

  return typing;
}

export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const measure = () => {
      /*
       * Publish what is visible, whatever the reason it changed.
       *
       * Written here rather than beside the inset below because it is not the
       * same fact: the inset is «сколько закрыто», which a platform can report
       * as nothing while covering half the screen, and this is «сколько
       * видно», which is measured directly and cannot be wrong.
       */
      document.documentElement.style.setProperty(
        VIEWPORT_HEIGHT,
        `${Math.round(viewport.height)}px`,
      );

      /*
       * `offsetTop` matters as much as the height: iOS scrolls the visual
       * viewport up rather than shrinking the page, so a keyboard shows up as
       * the window being taller than what is visible *below the offset*.
       */
      const covered = window.innerHeight - viewport.height - viewport.offsetTop;
      setInset(covered > KEYBOARD_MIN ? Math.round(covered) : 0);
    };

    measure();
    viewport.addEventListener("resize", measure);
    viewport.addEventListener("scroll", measure);

    return () => {
      viewport.removeEventListener("resize", measure);
      viewport.removeEventListener("scroll", measure);
      document.documentElement.style.removeProperty(VIEWPORT_HEIGHT);
    };
  }, []);

  return inset;
}

/**
 * Publish the measurement so `PANE_HEIGHT` can use it.
 *
 * On the root element rather than passed down: the panes that need it are four
 * screens deep and reach the number through a `calc` string, which cannot take
 * a prop.
 */
export function useChromeMetrics(hidden: boolean, inset: number): void {
  useEffect(() => {
    const root = document.documentElement;
    /*
     * The one place the bottom is accounted for, so that no one adds any part
     * of it a second time — see `BOTTOM_RESERVE`.
     */
    root.style.setProperty(CHROME_BOTTOM, hidden ? BOTTOM_RESERVE_TYPING : BOTTOM_RESERVE);
    root.style.setProperty(KEYBOARD_INSET, `${inset}px`);

    return () => {
      root.style.removeProperty(CHROME_BOTTOM);
      root.style.removeProperty(KEYBOARD_INSET);
    };
  }, [hidden, inset]);
}
